import { useState } from "react";
import { motion } from "framer-motion";
import { quizOptionLabel } from "@/lib/script";
import { MathRichText } from "@/lib/mathRichText";
import type { QuizSlideData } from "@/types/lesson";

interface QuizSlideProps {
  slide: QuizSlideData;
  onCorrect: () => void;
  isChildTheme: boolean;
}

export function QuizSlide({ slide, onCorrect, isChildTheme }: QuizSlideProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const answered = selected !== null;
  const correct = selected === slide.answer;
  const feedback = correct ? slide.feedback.correct : slide.feedback.incorrect;

  const handleSelect = (optLabel: string) => {
    if (answered) return;
    setSelected(optLabel);
    if (optLabel === slide.answer) onCorrect();
  };

  return (
    <motion.div
      className="h-full flex flex-col min-h-0 overflow-hidden rounded-lg"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.25 }}
    >
      <div className="px-4 py-2 rounded-t-lg bg-gradient-to-r from-violet-500 to-purple-600 text-white font-bold text-lg shrink-0" style={{ fontFamily: "var(--lp-font-heading)" }}>
        <MathRichText text={slide.title} childTheme />
      </div>
      {slide.decor && (
        <div className="flex justify-center gap-2 py-2 text-2xl shrink-0">
          {slide.decor.split("").filter(Boolean).map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
        <div className="rounded-lg border-2 border-purple-300 bg-purple-50/50 p-4">
          <div className="font-medium text-slate-800">
            <MathRichText text={slide.question} />
          </div>
        </div>
        <ul className="space-y-2">
          {slide.options.map((opt, idx) => {
            const label = quizOptionLabel(opt);
            const isSelected = selected === label;
            const showCorrect = answered && label === slide.answer;
            const showWrong = answered && isSelected && label !== slide.answer;
            return (
              <li key={`${idx}-${label}`}>
                <motion.button
                  type="button"
                  onClick={() => handleSelect(label)}
                  disabled={answered}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors disabled:cursor-default
                    ${!answered ? "border-blue-300 hover:bg-blue-100 hover:border-blue-400" : ""}
                    ${isSelected && !answered ? "border-blue-500 bg-blue-100" : ""}
                    ${showCorrect ? "border-green-500 bg-green-100 text-green-800" : ""}
                    ${showWrong ? "border-red-500 bg-red-100 text-red-800" : ""}
                  `}
                  whileHover={isChildTheme && !answered ? { scale: 1.01 } : undefined}
                  whileTap={isChildTheme ? { scale: 0.99 } : undefined}
                >
                  <MathRichText text={label} />
                  {showCorrect && " ✓"}
                  {showWrong && " ✗"}
                </motion.button>
              </li>
            );
          })}
        </ul>
        {answered && (
          <motion.div
            className={`rounded-lg p-4 flex gap-2 ${correct ? "bg-green-100 border border-green-300" : "bg-red-100 border border-red-300"}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <span className="text-xl shrink-0">{correct ? "✅" : "❌"}</span>
            <div className="text-slate-800 text-sm">
              <MathRichText text={feedback} />
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
